/*
 * File: BreakpointModal.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2023 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: April 30, 2025 at 4:42 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Components
import { Button } from '../Button';
import { Modal } from '../Modal';
import { Typography } from '../Typography';

// Styles
import * as S from './BreakpointModal.styles';


/**
 * Component
 */

export const BreakpointModal = ({
	className,
	isOpen,
	handleComplete,
	content,
	buttons
}) => {

	// Create state
	const [buttonsEnabled, setButtonsEnabled] = useState({});

	// Create refs
	const buttonsEnabledCurrent = useRef({});

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Handle actions on component load
	useEffect(() => {
		if (isOpen) {

			// Built enabled payload
			const enabledPayload = {};
			buttons.forEach((button) => {

				// Set enabled state
				enabledPayload[button.name] = !button.clickDelay;

				// Set timeout logic
				if (button.clickDelay) {
					setTimeout(() => {

						// Update buttons enabled
						const payload = { ...buttonsEnabledCurrent.current, [button.name]: true };
						setButtonsEnabled(payload);
						buttonsEnabledCurrent.current = payload;

					}, button.clickDelay);
				}
			});

			// Update buttons enabled
			setButtonsEnabled(enabledPayload);
			buttonsEnabledCurrent.current = enabledPayload;
		}
	}, [isOpen]);

	// Render component
	return (
		<Modal className={className} isOpen={isOpen} handleClose={handleComplete} showClose={false} clickOutsideClose={false} variant="large" useWrapper>
			<S.ModalInner>

				{/* Icon */}
				<S.IconContainer>
					<FontAwesomeIcon icon={['far', 'info-circle']} />
				</S.IconContainer>

				{/* Content */}
				{content.map(({ content: contentLocales }, index) => {
					const key = `c-${index}`;
					return (
						<Typography
							tag="p"
							variation="1"
							key={key}
							className="subtitle"
						>
							{contentLocales[locale.localeBaseId]}
						</Typography>
					);
				})}

				{/* Actions */}
				<S.ActionContainer>
					{buttons.map((button) => (
						<Button
							disabled={!buttonsEnabled[button.name]}
							variant={button.variant}
							variation={button.variation}
							onClick={handleComplete}
							key={button.name}
						>
							{button.label[locale.localeBaseId]}
						</Button>
					))}
				</S.ActionContainer>

			</S.ModalInner>
		</Modal>
	);
};


/**
 * Configuration
 */

BreakpointModal.displayName = 'BreakpointModal';
BreakpointModal.propTypes = {
	className: PropTypes.string,
	isOpen: PropTypes.bool,
	handleComplete: PropTypes.func,
	content: PropTypes.arrayOf(PropTypes.shape()),
	buttons: PropTypes.arrayOf(PropTypes.shape())
};
BreakpointModal.defaultProps = {
	className: null,
	isOpen: false,
	handleComplete: null,
	content: [],
	buttons: []
};
